import {
  onValue,
  ref,
  off,
  query,
  orderByChild,
  equalTo,
  limitToLast
} from "firebase/database";
import { create } from "zustand";
import { db } from "../lib/firebase";
import { useAuthStore } from "./auth.store";
import { User } from "../types/user.type";

interface UsersState {
  users: any[];
  user: User | null;
  showTour: boolean;
  setShowTour: (showTour: boolean) => void;
  isFavourite: (id: string) => boolean;
  userActivity: any;
  userFavourites: any;
  unreadActivity: number;
  loading: boolean;
  subscribeToUser: (uid: string) => () => void;
}

export const useUsersStore = create<UsersState>((set, get) => ({
  users: [],
  user: null,
  userActivity: null,
  userFavourites: null,
  showTour: false,
  isFavourite: (id: string) => {
    const favourites = get().userFavourites;
    if (!favourites) return false;
    const favourite = favourites.find(
      (favourite: any) => favourite.botId === id
    );
    return !!favourite;
  },
  setShowTour: (showTour: boolean) => set({ showTour }),
  unreadActivity: 0,
  loading: false,
  subscribeToUser: (uid: string) => {
    // console.log("subscribing to user", uid);
    const userRef = ref(db, `accounts/${uid}`);
    const listener: any = onValue(userRef, snapshot => {
      const data = snapshot.val() as User;
      if (data) {
        let updatedUsers = get().users;
        const existingUserIndex = updatedUsers.findIndex(
          user => user.userId === uid
        );
        if (existingUserIndex > -1) {
          updatedUsers[existingUserIndex] = data;
        } else {
          updatedUsers.push(data);
        }
        set({ users: updatedUsers });
      }
    });
    // Return an unsubscribe function
    return () => off(userRef, listener);
  }
}));

useAuthStore.subscribe(authState => {
  if (!authState.user) return;
  const userRef = ref(db, `accounts/${authState.user.uid}`);
  onValue(userRef, snapshot => {
    useUsersStore.setState({ loading: true });
    const data = snapshot.val();
    if (data) {
      useUsersStore.setState({ user: data, loading: false });
    }
  });

  const ordersRef = ref(db, "orders");
  onValue(
    query(
      ordersRef,
      orderByChild("user"),
      equalTo(authState.user.uid),
      limitToLast(50)
    ),
    snapshot => {
      const data = snapshot.val();
      // map key as id to each order
      if (!data) return;
      const userActivity = Object.keys(data)
        ?.map(key => ({ id: key, ...data[key] }))
        ?.reverse();
      if (userActivity) {
        const unreadActivity = userActivity?.filter(
          (activity: any) => !activity.read
        );
        useUsersStore.setState({
          userActivity,
          unreadActivity: unreadActivity.length
        });
      }
    }
  );

  const favouritesRef = ref(db, "savedStrategies");
  onValue(
    query(
      favouritesRef,
      orderByChild("user"),
      equalTo(authState.user.uid),
      limitToLast(50)
    ),
    snapshot => {
      const data = snapshot.val();
      if (!data) return;
      const userFavourites = Object.keys(data)
        ?.map(key => ({ id: key, ...data[key] }))
        ?.reverse();
      if (userFavourites) {
        useUsersStore.setState({
          userFavourites
        });
      }
    }
  );
});
